/**
 * Summarization Agent Detail View — tab orchestrator.
 *
 * Shows the expanded detail for a single summarization agent with tabs for:
 * stream, output, concerns, memory, and activity.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  ArrowLeft,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Brain,
  BarChart3,
  Activity,
  Database,
  BookOpen,
  TrendingDown,
  Hash,
  Eye,
  MessageSquare,
} from "lucide-react";
import { formatDuration } from "./shared";
import { StatusDot } from "./StatusBadges";
import { AgentStreamPanel } from "./AgentStreamPanel";
import { StructuredOutputView } from "./StructuredOutputView";
import { ConcernCard } from "./ConcernCard";
import { WorkingMemoryView } from "./WorkingMemoryView";
import { SummaryChatSheet } from "./SummaryChatSheet";
import { ExportButton } from "@/ui/composed/ExportButton";
import { ManagedAgentLivePanel } from "./ManagedAgentLivePanel";
import { ALL_MANIFEST_UI_METADATA } from "@cvx/jobs/summarization/agents/uiMetadata";

/**
 * Boundary shape for `task.workingMemory`. The backend query
 * (`getSummarizationTaskDetail`) declares this field `v.any()` — the
 * summarization pipeline owns its schema — so we type only the fields
 * this component reads.
 */
interface WorkingMemorySnapshot {
  pendingBatches?: number;
  findings?: unknown[];
  insolvencyIndicators?: unknown[];
  plan?: { status?: string }[];
}

interface SummarizationAgentDetailProps {
  taskId: Id<"summarizationTasks">;
  jobId: Id<"jobs">;
  summarizationRunId: Id<"summarizationRuns">;
  onBack: () => void;
  onRerun: () => void;
  isViewer?: boolean;
}

export function SummarizationAgentDetail({
  taskId,
  jobId,
  summarizationRunId: _summarizationRunId,
  onBack,
  onRerun,
  isViewer,
}: SummarizationAgentDetailProps) {
  const { data: task } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId },
    ),
  );

  // Managed Agents (v2) live stream — gated to super_admin during pilot
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const isSuperAdmin = currentUser?.systemRole === "super_admin";

  const [chatOpen, setChatOpen] = useState(false);

  if (!task) return null;

  // Manifest is the source of truth for the display label. Existing task rows
  // may have older names baked in (e.g. "Corporate Summary" → "Corporate");
  // override here so the UI updates immediately without backfilling the DB.
  const manifestName = ALL_MANIFEST_UI_METADATA.find(
    (m) => m.id === task.agentId,
  )?.name;
  const displayName = manifestName ?? task.agentName;

  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";

  // Working memory is `v.any()` on the wire — pin it to the local
  // boundary shape (see WorkingMemorySnapshot above).
  const workingMemory: WorkingMemorySnapshot | undefined = task.workingMemory;
  const concerns = task.concerns ?? [];
  const activityLog = task.activityLog ?? [];
  const plan = workingMemory?.plan ?? [];
  const planDoneCount = plan.filter((i) => i.status === "done").length;

  // Batch progress info from working memory
  const pendingBatches = workingMemory?.pendingBatches;
  const totalBatchesFromLog = activityLog.find((e) =>
    e.message?.includes("parallel analysis batches"),
  );
  const totalBatchCount = totalBatchesFromLog
    ? parseInt(totalBatchesFromLog.message.match(/(\d+) parallel/)?.[1] ?? "0")
    : undefined;
  const completedBatches =
    totalBatchCount !== undefined && pendingBatches !== undefined
      ? totalBatchCount - pendingBatches
      : undefined;
  const isParallelExecute =
    isRunning &&
    task.currentPhase === "execute" &&
    pendingBatches !== undefined &&
    pendingBatches > 0;

  const findingsCount = workingMemory?.findings?.length ?? 0;
  const indicatorsCount = workingMemory?.insolvencyIndicators?.length ?? 0;

  return (
    <Tabs
      defaultValue={isRunning ? "stream" : isCompleted ? "output" : "stream"}
      className="-m-6 flex flex-col bg-muted px-6 pb-6 [overflow-anchor:none]"
    >
      {/* Sticky header + tab bar — pins below the dashboard header */}
      <div className="sticky top-16 z-[9] -mx-6 bg-muted px-6 pb-2 pt-6">
        <div className="flex items-center gap-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <StatusDot status={task.status} />
            <h3 className="truncate text-lg font-semibold">{displayName}</h3>
            <div className="flex gap-1.5">
              {task.categories.map((cat: string) => (
                <Badge key={cat} variant="secondary" className="text-xs">
                  {cat}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {task.currentPhase && isRunning && (
              <Badge
                variant="outline"
                className="border-blue-200 bg-blue-50 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300"
              >
                {task.currentPhase === "orient" && "Orienting"}
                {task.currentPhase === "execute" &&
                  (isParallelExecute
                    ? `Analysing (${completedBatches ?? 0}/${totalBatchCount ?? "?"} batches)`
                    : "Executing")}
                {task.currentPhase === "reflect" && "Reflecting"}
                {task.currentPhase === "synthesize" && "Synthesising"}
              </Badge>
            )}
            {task.duration && (
              <Badge variant="outline" className="text-xs tabular-nums">
                {formatDuration(task.duration)}
              </Badge>
            )}
            {task.documentCount > 0 && (
              <Badge variant="outline" className="text-xs">
                {task.documentsAnalysed}/{task.documentCount} docs
              </Badge>
            )}
            {isCompleted && task.output && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatOpen(true)}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Chat
              </Button>
            )}
            {isCompleted && task.output && (
              <ExportButton
                content={{
                  title: displayName,
                  output: task.output as Record<string, unknown>,
                }}
                filename={`${displayName.replace(/\s+/g, "_")}_Summary`}
              />
            )}
            {(isRunning || isCompleted || isFailed) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRerun}
                disabled={isViewer}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Re-run
              </Button>
            )}
          </div>
        </div>

        <TabsList className="w-full">
          <TabsTrigger value="stream" className="gap-1.5">
            <Brain className="h-3.5 w-3.5" />
            Agent Stream
            {isRunning && (
              <span className="relative ml-1 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="output" className="gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" />
            Output
            {isCompleted && task.output && (
              <CheckCircle2 className="ml-0.5 h-3 w-3 text-emerald-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="concerns" className="gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            Concerns
            {concerns.length > 0 && (
              <Badge variant="destructive" size="sm" className="ml-0.5">
                {concerns.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="memory" className="gap-1.5">
            <Database className="h-3.5 w-3.5" />
            Memory
            {plan.length > 0 && (
              <Badge variant="secondary" size="sm" className="ml-0.5">
                {planDoneCount}/{plan.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-1.5">
            <Activity className="h-3.5 w-3.5" />
            Activity
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Parallel batch progress banner */}
      {isParallelExecute && (
        <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3 dark:border-blue-800 dark:bg-blue-950/20">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-900 dark:text-blue-100">
                <span>Parallel analysis in progress</span>
                <span className="tabular-nums text-blue-600 dark:text-blue-400">
                  {completedBatches ?? 0}/{totalBatchCount ?? "?"} batches
                  complete
                </span>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-blue-700/80 dark:text-blue-300/60">
                {findingsCount > 0 && (
                  <span className="flex items-center gap-1">
                    <BookOpen className="h-3 w-3" />
                    {findingsCount} findings
                  </span>
                )}
                {indicatorsCount > 0 && (
                  <span className="flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" />
                    {indicatorsCount} indicators
                  </span>
                )}
                {findingsCount === 0 && indicatorsCount === 0 && (
                  <span>
                    Sub-agents are analysing documents concurrently...
                  </span>
                )}
              </div>
            </div>
            {totalBatchCount && totalBatchCount > 0 && (
              <div className="h-2 w-24 overflow-hidden rounded-full bg-blue-200 dark:bg-blue-800">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-500 dark:bg-emerald-400"
                  style={{
                    width: `${((completedBatches ?? 0) / totalBatchCount) * 100}%`,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab content */}
      <TabsContent value="stream" className="mt-2">
        {isParallelExecute && (
          <div className="mb-3 flex items-center gap-2 rounded-md bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <Eye className="h-3.5 w-3.5" />
            <span>
              Showing primary batch stream. {pendingBatches} other batch
              {pendingBatches === 1 ? "" : "es"} running in parallel — check
              Activity tab for all progress.
            </span>
          </div>
        )}
        <AgentStreamPanel
          threadId={task.threadId}
          jobId={jobId}
          status={task.status}
        />
      </TabsContent>

      <TabsContent value="output" className="mt-2">
        {task.output ? (
          <StructuredOutputView output={task.output} />
        ) : task.error ? (
          <div className="py-8 text-center">
            <XCircle className="mx-auto mb-2 h-8 w-8 text-destructive" />
            <p className="text-sm text-destructive">{task.error}</p>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {isRunning
              ? "Output will appear when the agent completes..."
              : "No output yet. Start the agent to begin analysis."}
          </div>
        )}
      </TabsContent>

      <TabsContent value="concerns" className="mt-2">
        {concerns.length > 0 ? (
          <div className="flex flex-col gap-3 pr-4">
            {concerns.map((concern, i) => (
              <ConcernCard key={i} concern={concern} />
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No concerns flagged{isRunning ? " yet" : ""}.
          </p>
        )}
      </TabsContent>

      <TabsContent value="memory" className="mt-2">
        {task.workingMemory ? (
          <WorkingMemoryView
            memory={task.workingMemory}
            isRunning={isRunning}
          />
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No working memory yet. The agent builds memory as it works through
            its plan.
          </p>
        )}
      </TabsContent>

      <TabsContent value="activity" className="mt-2">
        {isSuperAdmin && (task.runnerVersion || task.liveText) && (
          <ManagedAgentLivePanel
            liveText={task.liveText ?? ""}
            isRunning={isRunning}
            anthropicAgentId={task.anthropicAgentId}
            anthropicAgentVersion={task.anthropicAgentVersion}
            anthropicSessionId={task.anthropicSessionId}
            runnerVersion={task.runnerVersion}
          />
        )}
        {activityLog.length > 0 ? (
          <div className="flex flex-col gap-0 pr-4">
            {activityLog.map((entry, i) => (
              <div
                key={i}
                className="flex gap-3 border-b border-muted/30 py-2 text-sm last:border-0"
              >
                <span className="mt-0.5 whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </span>
                <span className="text-foreground/80">{entry.message}</span>
              </div>
            ))}
            {isRunning && (
              <div className="flex items-center gap-2 py-2 text-sm text-blue-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Agent is working...</span>
              </div>
            )}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No activity logged yet.
          </p>
        )}
      </TabsContent>

      {/* Footer — Token usage */}
      {task.tokensUsed && (
        <div className="mt-4 flex items-center gap-4 border-t pt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Hash className="h-3 w-3" /> Tokens:
          </span>
          <span className="tabular-nums">
            {task.tokensUsed.input.toLocaleString()} in
          </span>
          <span className="tabular-nums">
            {task.tokensUsed.output.toLocaleString()} out
          </span>
          {task.tokensUsed.cacheRead != null &&
            task.tokensUsed.cacheRead > 0 && (
              <span className="tabular-nums text-emerald-600">
                {task.tokensUsed.cacheRead.toLocaleString()} cached
              </span>
            )}
          {task.retryCount != null && task.retryCount > 0 && (
            <span className="text-amber-600">{task.retryCount} retries</span>
          )}
        </div>
      )}

      {/* Chat slideout */}
      {isCompleted && task.output && (
        <SummaryChatSheet
          open={chatOpen}
          onOpenChange={setChatOpen}
          taskId={taskId}
          jobId={jobId}
          agentName={displayName}
        />
      )}
      <AiDisclaimer className="mt-4 text-center" />
    </Tabs>
  );
}
